'use client'

import * as React from 'react'
import * as Dialog from 'primitives/dialog'
import { usePathname, useRouter } from 'next/navigation'
import { Z_INDEX_MODALS } from 'constants/modals'
import { RichTextInput } from '../rich-text-input'
import { IconClose, IconArrowBack, IconError } from '@repo/design-system-cf/components/icons'
import { Button } from '@repo/design-system-cf/components/button'
import { usePostProductProjectMutation } from '@web/gen/service/creativefabrica/social/v2'
import { SkillLevel } from '@web/gen/service/creativefabrica/social/v2/types_pb'
import { useFeatureImageUpload } from '../../_hooks/use-feature-image-upload'
import { useQueryClient } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import { socialCuratedFeedQueryRootKey, socialCuratedFeedQueryKey } from '@web/gen/feed'
import type { SocialCuratedFeedQuery } from '@web/gen/feed'
import type { ProjectLevel } from '@web/gen/types'
import { PostType, SocialAssetType } from '@web/gen/types'
import toast from 'react-hot-toast'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useUserQuery } from 'auth/hooks'
import {
  AssetSection,
  AssetSectionMobileContent,
} from '../create-post-project-dialog/_components/asset-section'
import { MachinesInputMobileContent } from '../create-post-project-dialog/_components/machines-input'
import { MaterialsInputMobileContent } from '../create-post-project-dialog/_components/materials-input'
import { ThemesInputMobileContent } from '../create-post-project-dialog/_components/themes-input'
import { useCreatePostProjectDialog } from './create-post-project-dialog-context'
import { FileDropzone } from './_components/file-drop-zone'
import { FileGallery } from './_components/file-gallery'
import { tw } from 'utils/classnames'
import { useTranslations } from 'next-intl'
import { useVideoUpload } from '../../_hooks/use-video-upload'
import {
  ProjectDraftProvider,
  useProjectDraft,
} from '../create-post-project-dialog/project-draft-context'
import type { UploadPreview } from '../create-post-project-dialog/project-draft-context'
import { OptionalRightSection } from './_components/optional-right-section'
import { AddMediaSection } from './_components/add-media-section'
import { convertImageToWebp } from '../../utils/image-convertion'
import { useFeedRoute } from '../../contexts/feed-route-context'

const MAX_IMAGE_FILE_SIZE = 20 * 1024 * 1024 // 20MB
const MAX_VIDEO_FILE_SIZE = 2 * 1024 * 1024 * 1024 // 2GB

// Outer component: provides the draft context
export function CreatePostProjectDialog() {
  return (
    <ProjectDraftProvider>
      <CreatePostProjectDialogInner />
    </ProjectDraftProvider>
  )
}

const ALLOWED_VIDEO_MIME_TYPES = new Set(['video/mp4', 'video/quicktime', 'video/webm'])
// oxlint-disable-next-line unicorn/prefer-set-has
const ALLOWED_VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm']

function validateMediaRules(existing: UploadPreview[], incoming: File[]): string | null {
  const existingImages = existing.filter(u => u.type === 'image').length
  const newImages = incoming.filter(f => !f.type.startsWith('video')).length

  // Rule: max 10 images total
  if (existingImages + newImages > 10) {
    return 'You can upload up to 10 images'
  }

  return null // All good
}

// Inner component: uses the draft
// oxlint-disable-next-line complexity
function CreatePostProjectDialogInner() {
  const t = useTranslations('components.create_product_project_dialog')
  const queryClient = useQueryClient()
  const pathname = usePathname()
  const router = useRouter()

  const { isOpen, closeDialog, mobileView, closeMobileView } = useCreatePostProjectDialog()
  const { basePath } = useFeedRoute()

  const { data: user } = useUserQuery()
  const slug = user?.me?.user?.slug

  const { uploadVideo } = useVideoUpload()
  const { uploadFeaturedImages } = useFeatureImageUpload()

  const {
    description,
    setDescription,
    uploads,
    setUploads,
    assets: selectedAssets,
    resetDraft,
    machines,
    materials,
    theme,
    skillLevel: skillLevelValue,
    activeInstructionMedia,
    steps,
    setSteps,
    setHasValidated,
  } = useProjectDraft()

  const [isDragging, setIsDragging] = React.useState(false)
  const [deletingIndex, setDeletingIndex] = React.useState<number | null>(null)
  const [fileError, setFileError] = React.useState<string | null>(null)
  const [highlightDescription, setHighlightDescription] = React.useState(false)
  const [highlightMedia, setHighlightMedia] = React.useState(false)
  const [uploadingError, setUploadingError] = React.useState(false)

  const descriptionRef = React.useRef<HTMLDivElement>(null)
  const mediaRef = React.useRef<HTMLDivElement>(null)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const lastScrollRef = React.useRef(0)
  const previousCuratedData = React.useRef<InfiniteData<SocialCuratedFeedQuery> | undefined>(
    undefined,
  )

  const postMutation = usePostProductProjectMutation()

  const isOnProjectPage =
    (pathname?.startsWith(AppRoutes.SOCIAL_FEED_DISCOVERY_FEED) &&
      pathname !== AppRoutes.SOCIAL_FEED_DISCOVERY_FEED) ||
    (pathname?.startsWith(AppRoutes.SOCIAL_CURATED_FEED) &&
      pathname !== AppRoutes.SOCIAL_CURATED_FEED)
  const isSavedFeedPage =
    pathname === AppRoutes.SOCIAL_FEED_SAVED_POSTS ||
    pathname === AppRoutes.SOCIAL_CURATED_FEED_SAVED
  const isOwnedByCrafter = slug && pathname?.endsWith(`${slug}/`)
  const isRedirectingAfterPost = isOnProjectPage || isSavedFeedPage || !isOwnedByCrafter

  const validateFiles = (files: File[]): boolean => {
    // reject disallowed video formats
    const invalidFormat = files.find(file => {
      if (!file.type.startsWith('video')) {
        return false
      }

      // check MIME
      if (ALLOWED_VIDEO_MIME_TYPES.has(file.type)) {
        return false
      }

      // fallback: check extension (in case type is missing/odd)
      const dotIndex = file.name.lastIndexOf('.')
      // oxlint-disable-next-line no-negated-condition
      const ext = dotIndex !== -1 ? file.name.slice(dotIndex).toLowerCase() : ''
      const isAllowedExt = ALLOWED_VIDEO_EXTENSIONS.includes(ext)

      return !isAllowedExt
    })

    if (invalidFormat) {
      setFileError('Only MP4, MOV and WEBM videos are supported')
      return false
    }

    const ruleError = validateMediaRules(uploads, files)
    if (ruleError) {
      setFileError(ruleError)
      return false
    }

    // size limit
    // size limit (different for images vs videos)
    const tooLarge = files.find(f => {
      const isVideo = f.type.startsWith('video')
      const limit = isVideo ? MAX_VIDEO_FILE_SIZE : MAX_IMAGE_FILE_SIZE
      return f.size > limit
    })

    if (tooLarge) {
      const isVideo = tooLarge.type.startsWith('video')
      setFileError(isVideo ? 'Video exceeds size limit of 2GB' : 'Image exceeds size limit of 20MB')
      return false
    }

    setFileError(null)
    return true
  }

  const scrollAndHighlight = (
    ref: React.RefObject<HTMLDivElement | null> | undefined,
    setHighlight: (v: boolean) => void,
  ) => {
    if (!ref?.current || !scrollContainerRef.current) {
      return
    }

    // Scroll smoothly
    scrollContainerRef.current.scrollTo({
      top: ref.current.offsetTop - 40,
      behavior: 'smooth',
    })

    // Flash highlight
    setHighlight(true)
  }

  const handleDeleteClick = (index: number) => {
    setDeletingIndex(index)
    setTimeout(() => {
      setUploads(prev => prev.filter((_, i) => i !== index))
      setDeletingIndex(null)
    }, 250)
  }

  // Inside CreatePostProjectDialogInner component
  // oxlint-disable-next-line complexity
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files
    if (!fileList?.length) {
      return
    }
    if (uploads.length >= 10) {
      return
    }

    const remainingSlots = 10 - uploads.length
    const rawFiles = [...fileList].slice(0, remainingSlots)

    if (!validateFiles(rawFiles)) {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    // 1. Initial Previews (fast UI response)
    const previews: UploadPreview[] = rawFiles.map(f => ({
      tempId: crypto.randomUUID(),
      url: URL.createObjectURL(f),
      type: f.type.startsWith('video') ? 'video' : 'image',
      uploading: true,
    }))

    setUploads(prev => [...previews, ...prev])

    try {
      const completed: UploadPreview[] = []

      for (let i = 0; i < rawFiles.length; i++) {
        const file = rawFiles[i]
        const tempId = previews[i].tempId

        if (file.type.startsWith('video')) {
          const notif = await uploadVideo(file)
          const upload = notif.social?.feedPayload?.uploadProcessed?.upload
          completed.push({
            tempId,
            id: upload?.id,
            url: upload?.video?.thumbnailUrls?.[0] ?? '',
            type: 'video',
            uploading: false,
          })
        } else {
          let fileToUpload = file
          try {
            // Convert to WebP (handles HEIC and standard images)
            fileToUpload = await convertImageToWebp(file)
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Image conversion failed, using original', error)
          }

          const [notif] = await uploadFeaturedImages([fileToUpload])
          const upload = notif?.social?.feedPayload?.uploadProcessed?.upload
          completed.push({
            tempId,
            id: upload?.id,
            url: upload?.image?.thumbnails?.[0]?.url || upload?.image?.previewUrl || '',
            type: 'image',
            uploading: false,
          })
        }
      }

      setUploads(prev => {
        const next = [...prev]
        for (const processed of completed) {
          const idx = next.findIndex(u => u.tempId === processed.tempId)
          if (idx !== -1) {
            next[idx] = processed
          }
        }
        return next
      })
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)

    if (!e.dataTransfer.files.length) {
      return
    }

    const remainingSlots = 10 - uploads.length
    const files = [...e.dataTransfer.files].slice(0, remainingSlots)

    const transfer = new DataTransfer()
    files.forEach(f => transfer.items.add(f))

    if (fileInputRef.current) {
      fileInputRef.current.files = transfer.files
      fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  const triggerUpload = () => fileInputRef.current?.click()

  const disabledPost =
    !description || uploads.filter(u => u.id).length === 0 || uploads.some(u => u.uploading)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const isUploading = uploads.some(u => u.uploading)
    if (isUploading) {
      setUploadingError(true)
      scrollAndHighlight(mediaRef, setHighlightMedia)
      return
    }

    if (!description) {
      scrollAndHighlight(descriptionRef, setHighlightDescription)
    }

    const hasUploads = uploads.filter(u => u.id).length > 0
    if (!hasUploads) {
      scrollAndHighlight(mediaRef, setHighlightMedia)
    }

    // If either is missing — stop submit
    if (!description || !hasUploads) {
      return
    }

    // always trigger validation state for UI
    setHasValidated(true)

    // If there is only 1 step → skip validation completely
    if (steps.length === 1) {
      // Ensure no leftover error
      setSteps(prev => [{ ...prev[0], isValid: undefined }])
    }

    // Validate only when 2 or more steps exist
    if (steps.length > 1) {
      const validatedSteps = steps.map(s => ({
        ...s,
        isValid: s.description.trim().length > 0 || s.media.length > 0,
      }))

      setSteps(validatedSteps)

      if (validatedSteps.some(s => !s.isValid)) {
        return
      }
    }

    const imageIds = uploads.filter(u => u.type === 'image' && u.id).map(u => u.id!)
    const videoIds = uploads.filter(u => u.type === 'video' && u.id).map(u => u.id!)

    const assetIds = selectedAssets.map(a => a.id)
    const machineIds = machines.map(m => Number(m.id))
    const materialIds = materials.map(m => Number(m.id))
    const themeIds = theme ? [Number(theme.id)] : []
    const skillLevel: SkillLevel | undefined =
      skillLevelValue !== null && skillLevelValue !== SkillLevel.UNSPECIFIED
        ? skillLevelValue
        : undefined
    const instructionPayload = steps
      .map(step => {
        const uploadIds = step.media.filter(m => m.type === 'image' && m.id).map(m => m.id!)

        const videoIds = step.media.filter(m => m.type === 'video' && m.id).map(m => m.id!)

        const hasContent =
          step.description.trim().length > 0 || uploadIds.length > 0 || videoIds.length > 0

        if (!hasContent) {
          return null
        }

        return {
          description: step.description,
          uploadIds,
          videoIds,
        }
      })
      .filter(Boolean) as {
      description: string
      uploadIds: string[]
      videoIds: string[]
    }[]

    const instructions = instructionPayload.length > 0 ? instructionPayload : null // fix empty array to null

    if (!description || (imageIds.length === 0 && videoIds.length === 0)) {
      return
    }

    const isFeedRoute = basePath === '/feed'
    const curatedFeedQueryKey = [
      socialCuratedFeedQueryRootKey,
      ...socialCuratedFeedQueryKey({ input: { itemsPerPage: 3 } }),
    ]

    // Build optimistic post for /feed route
    const optimisticPost: SocialCuratedFeedQuery['socialCuratedFeed']['posts'] extends
      | (infer P)[]
      | null
      | undefined
      ? P
      : never = isFeedRoute
      ? {
          __typename: 'Post' as const,
          id: `optimistic-${crypto.randomUUID()}`,
          postSlug: null,
          content: description,
          likesCount: 0,
          isLiked: false,
          isSaved: false,
          commentsCount: 0,
          type: PostType.Product,
          machines: null,
          materials: null,
          instructions: null,
          themes: null,
          level: null,
          createTime: new Date().toISOString(),
          childComments: null,
          user: {
            __typename: 'PublicUserProfile' as const,
            id: user?.me?.user?.id ?? '',
            slug: user?.me?.user?.slug ?? null,
            displayName:
              [user?.me?.user?.firstName, user?.me?.user?.lastName].filter(Boolean).join(' ') ||
              null,
            firstName: user?.me?.user?.firstName ?? null,
            lastName: user?.me?.user?.lastName ?? null,
            shopName: null,
            avatarUrl: user?.me?.user?.avatarUrl ?? null,
            superSkill: null,
            isCreativeFabrica: null,
            isVerified: null,
            isPremium: null,
            isFollowed: null,
            following: false,
            isTemporary: false,
            profileUrls: null,
            location: { __typename: 'UserLocation' as const, country: '' as never },
          },
          socialProducts: null,
          assets: uploads
            .filter(u => u.id)
            .map(u => ({
              __typename: 'SocialAsset' as const,
              id: u.id!,
              url: u.url,
              optimizedUrl: u.url,
              type: u.type === 'video' ? SocialAssetType.Video : SocialAssetType.Image,
              thumbnailUrls: [{ __typename: 'Thumbnail' as const, url: u.url, width: 400 }],
              videoInfo: null,
            })),
          machineItems: null,
          materialItems: null,
          themeItems: null,
          instructionItems: null,
        }
      : (null as never)

    // Optimistic update for /feed route: update cache and close dialog before mutation
    if (isFeedRoute) {
      previousCuratedData.current =
        queryClient.getQueryData<InfiniteData<SocialCuratedFeedQuery>>(curatedFeedQueryKey)

      if (previousCuratedData.current?.pages?.[0]) {
        const updatedPages = [...previousCuratedData.current.pages]
        const firstPage = updatedPages[0]
        updatedPages[0] = {
          ...firstPage,
          socialCuratedFeed: {
            ...firstPage.socialCuratedFeed,
            posts: [optimisticPost, ...(firstPage.socialCuratedFeed.posts ?? [])],
          },
        }

        queryClient.setQueryData<InfiniteData<SocialCuratedFeedQuery>>(curatedFeedQueryKey, {
          ...previousCuratedData.current,
          pages: updatedPages,
        })
      }

      closeMobileView()
      closeDialog()
      resetDraft()
    }

    postMutation.mutate(
      {
        description,
        uploadIds: imageIds,
        videoIds,
        assetIds,
        machineIds,
        materialIds,
        themeIds,
        skillLevel,
        // @ts-ignore
        instructions,
      },
      {
        onSuccess: response => {
          if (!isFeedRoute) {
            closeMobileView()
            closeDialog()
            resetDraft()
          }

          // Replace the optimistic post's fake ID and slug with real ones from the server
          if (isFeedRoute && response.project?.id) {
            const {
              id: realId,
              postSlug: realPostUrl,
              level: realLevel,
              instructionItems: realInstructions,
            } = response.project
            queryClient.setQueryData<InfiniteData<SocialCuratedFeedQuery>>(
              curatedFeedQueryKey,
              old => {
                if (!old) {
                  return old
                }
                return {
                  ...old,
                  pages: old.pages.map(page => ({
                    ...page,
                    socialCuratedFeed: {
                      ...page.socialCuratedFeed,
                      posts: (page.socialCuratedFeed.posts ?? []).map(post =>
                        post.id === optimisticPost.id
                          ? {
                              ...post,
                              id: realId,
                              postSlug: realPostUrl?.split('/').filter(Boolean).pop() ?? null,
                              level: (realLevel as unknown as ProjectLevel) ?? null,
                              instructionItems: realInstructions ?? null,
                            }
                          : post,
                      ),
                    },
                  })),
                }
              },
            )
          }

          queryClient.invalidateQueries({ queryKey: ['socialDiscoveryFeed'], exact: false })
          queryClient.invalidateQueries({ queryKey: ['socialUserFeed.Infinite'], exact: false })
          queryClient.invalidateQueries({ queryKey: ['socialSavedFeed.Infinite'], exact: false })

          if (!isFeedRoute) {
            queryClient.invalidateQueries({
              queryKey: [socialCuratedFeedQueryRootKey],
              exact: false,
            })
          }

          if (isRedirectingAfterPost) {
            router.push(
              isFeedRoute ? AppRoutes.SOCIAL_CURATED_FEED : AppRoutes.SOCIAL_FEED_DISCOVERY_FEED,
            )
          }

          toast.success('Your project was successfully posted')
        },
        onError: () => {
          if (isFeedRoute && previousCuratedData.current) {
            queryClient.setQueryData<InfiniteData<SocialCuratedFeedQuery>>(
              curatedFeedQueryKey,
              previousCuratedData.current,
            )
          }
          toast.error('Failed to post your project. Please try again.')
        },
      },
    )
  }

  const handleCloseDialog = () => {
    closeMobileView()
    closeDialog()
  }

  React.useEffect(() => {
    if (mobileView && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }, [mobileView])

  React.useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) {
      return
    }

    if (activeInstructionMedia) {
      // Save current scroll before opening AddMediaSection
      lastScrollRef.current = container.scrollTop

      // Scroll to top when AddMediaSection opens
      container.scrollTop = 0
    } else {
      // Restore scroll when AddMediaSection closes
      container.scrollTop = lastScrollRef.current
    }
  }, [activeInstructionMedia])

  React.useEffect(() => {
    const hasUploads = uploads.some(u => u.id)
    if (highlightMedia && hasUploads) {
      setHighlightMedia(false)
    }
  }, [uploads, highlightMedia])

  React.useEffect(() => {
    // If the user selects files → hide the "add at least 1 image" error immediately
    if (uploads.length > 0 && highlightMedia) {
      setHighlightMedia(false)
    }
  }, [uploads.length, highlightMedia])

  React.useEffect(() => {
    const stillUploading = uploads.some(u => u.uploading)
    if (!stillUploading && uploadingError) {
      setUploadingError(false)
    }
  }, [uploads, uploadingError])

  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && handleCloseDialog()}>
      <React.Suspense fallback={null}>
        <Dialog.Portal>
          <Dialog.Body
            tabIndex={-1}
            className="font-poppins z-50 flex justify-center items-center fixed inset-0"
            overlayZIndex={Z_INDEX_MODALS.default}
            zIndex={Z_INDEX_MODALS.default}
          >
            <div
              className="sm:rounded-[12px] mt-6 tablet:mt-0  rounded-t-4 border border-[#1313151F] bg-cf-white w-full max-w-screen-xl
                sm:max-h-[95vh] flex flex-col p-0 absolute sm:relative bottom-0 top-0 sm:top-auto"
            >
              <div
                ref={scrollContainerRef}
                className={tw(
                  'relative tablet:p-6',
                  mobileView ? 'overflow-hidden pb-0' : 'overflow-y-auto',
                )}
              >
                {mobileView ? (
                  // Mobile “overlay” mode
                  <div className="flex flex-col sm:h-auto">
                    <div className="flex items-center gap-3 sm:hidden pb-4 px-4 pt-4">
                      <Button
                        type="button"
                        size="regular"
                        variant="tertiary"
                        className="!px-0"
                        onClick={closeMobileView}
                        iconLeft={<IconArrowBack className="!size-4 text-cf-purple-900" />}
                      />
                      <p className="text-16 font-semibold text-cf-grey-1000">
                        {mobileView.type === 'assets' ? 'Link assets' : null}
                        {mobileView.type === 'machines' ? 'Add machines' : null}
                        {mobileView.type === 'materials' ? 'Add materials' : null}
                        {mobileView.type === 'themes' ? 'Add themes' : null}
                      </p>
                    </div>
                    {mobileView.type === 'assets' ? (
                      <AssetSectionMobileContent onClose={closeMobileView} />
                    ) : null}
                    {mobileView.type === 'machines' ? (
                      <MachinesInputMobileContent onClose={closeMobileView} />
                    ) : null}
                    {mobileView.type === 'materials' ? (
                      <MaterialsInputMobileContent onClose={closeMobileView} />
                    ) : null}
                    {mobileView.type === 'themes' ? (
                      <ThemesInputMobileContent onClose={closeMobileView} />
                    ) : null}
                  </div>
                ) : activeInstructionMedia ? (
                  <AddMediaSection />
                ) : (
                  // Main form mode
                  <form onSubmit={handleSubmit}>
                    <div className="flex tablet:flex-row flex-col items-start tablet:items-center justify-start px-5 tablet:px-0 pt-4 tablet:pt-0 tablet:gap-4 pb-4">
                      <p className="tablet:text-24 text-20 font-bold text-cf-grey-800 ">
                        {t('post_project_title')}
                      </p>
                    </div>
                    <div className="flex flex-col tablet:flex-row gap-4 w-full px-5 tablet:px-0">
                      <div className="tablet:w-1/3 tablet:max-w-[374px] tablet:min-w-[296px] w-full tablet:p-6 p-0 tablet:border rounded-[12px] tablet:bg-cf-grey-25 tablet:border-cf-transparent-grey-1000-12">
                        <p className="text-18 font-bold text-nowrap text-cf-grey-800 block">
                          1. Describe your project
                        </p>
                        <p className="text-12 text-cf-grey-800 font-bold mt-4 mb-2">
                          Add a description
                        </p>
                        <div>
                          {/* <p className="text-14 text-cf-grey-800 pb-6">
                            Add up to 10 photos of a project you would like to share. Add project
                            information such as materials and tools used.
                          </p> */}
                          <div ref={descriptionRef}>
                            <RichTextInput
                              value={description}
                              onChange={value => {
                                setDescription(value)
                                if (highlightDescription && value.trim().length > 0) {
                                  setHighlightDescription(false)
                                }
                              }}
                              onTriggerImageUpload={triggerUpload}
                              imageCount={uploads.length}
                              placeholder="Type your post here"
                              isError={highlightDescription}
                            />
                            {highlightDescription ? (
                              <p className="mt-2 text-cf-red-900 text-12 flex items-center">
                                <IconError className="!size-3" />
                                This field is required
                              </p>
                            ) : null}
                          </div>

                          <>
                            <p className="text-12 text-cf-grey-800 font-bold mt-4 mb-2">
                              Add at least 1 image or video
                            </p>
                            <div ref={mediaRef}>
                              {uploads.length === 0 ? (
                                <FileDropzone
                                  isDragging={isDragging}
                                  setIsDragging={setIsDragging}
                                  handleDrop={handleDrop}
                                  triggerUpload={triggerUpload}
                                  isError={highlightMedia || !!fileError}
                                />
                              ) : (
                                <FileGallery
                                  uploads={uploads}
                                  deletingIndex={deletingIndex}
                                  onDelete={handleDeleteClick}
                                  onAddMore={triggerUpload}
                                />
                              )}
                            </div>

                            {uploadingError ? (
                              <p className="mt-2 text-cf-red-900 text-12 flex items-center gap-1">
                                <IconError className="!size-3" />
                                Please wait until all files finish uploading
                              </p>
                            ) : null}

                            {highlightMedia ? (
                              <p className="mt-2 text-cf-red-900 text-12 flex items-center gap-1">
                                <IconError className="!size-3" />
                                Upload at least 1 photo or video
                              </p>
                            ) : null}

                            {fileError ? (
                              <p className="mt-2 text-cf-red-900 text-12 flex items-center gap-1">
                                <IconError className="!size-3" />
                                {fileError}
                              </p>
                            ) : null}
                          </>

                          <div className="pt-4">
                            <p className="text-12 text-cf-grey-800 font-bold pb-2">
                              {t('assets_section.assets_title')}
                              <span className="font-normal">{t('optional')}</span>
                            </p>

                            <AssetSection />
                          </div>
                        </div>
                      </div>
                      <div className="tablet:w-2/3 w-full">
                        <OptionalRightSection />
                      </div>
                    </div>
                    <div className="sticky tablet:relative bottom-0 bg-cf-white z-50 border-t tablet:border-t-0 border-cf-transparent-grey-1000-12 mt-6 tablet:mt-0 flex items-center justify-end pt-4 tablet:pt-6 pb-6 tablet:pb-0">
                      <div className="w-full tablet:w-auto px-5 tablet:px-0 ">
                        <Button
                          data-testid="post-project-dialog-submit-button"
                          className="w-full"
                          type="submit"
                        >
                          {t('post_your_project_button')}
                        </Button>
                      </div>
                    </div>
                  </form>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.mp4,.mov,.webm,video/mp4,video/quicktime,video/webm"
                  multiple
                  className="hidden"
                  onChange={handleFileUpload}
                />

                {!mobileView && !activeInstructionMedia ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="!size-9 text-cf-purple-900 absolute right-2 top-4"
                    onClick={handleCloseDialog}
                  >
                    <IconClose className="h-full w-full" />
                  </Button>
                ) : null}
              </div>
            </div>
          </Dialog.Body>
        </Dialog.Portal>
      </React.Suspense>
    </Dialog.Root>
  )
}
